/**
 * The Chama gate: figures out where the logged-in user stands with the
 * community — founder/moderator, member, invited, application pending,
 * declined, or a stranger who should see the application form.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNostr } from "@nostrify/react";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  COMMUNITY_RELAYS,
  KIND_COMMUNITY_LIST,
  KIND_DIRECT_INVITE,
  JOIN_RESPONSE_KIND,
  MODERATOR_PUBKEY,
} from "@/lib/concord/config";
import { decryptCommunityList, type CommunityListState } from "@/lib/concord/membership";
import { unwrapDirectInvite, type ReceivedInvite } from "@/lib/concord/invites";
import { unwrapJoinResponse, type ReceivedJoinResponse } from "@/lib/concord/requests";
import type { JoinRequest, Membership } from "@/lib/concord/types";

export type ChamaStatus =
  | "loading"
  | "logged-out"
  | "unsupported" // signer can't do NIP-44
  | "stranger" // logged in, no relationship yet
  | "pending" // application submitted, awaiting review
  | "invited" // a valid Direct Invite is waiting
  | "declined" // the moderator declined (may reapply)
  | "member";

export interface ApplicationRecord {
  at: number;
  request: JoinRequest;
}

const EMPTY_LIST: CommunityListState = { memberships: [], fragments: new Map() };

function readApplication(key: string): ApplicationRecord | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as ApplicationRecord) : null;
  } catch {
    return null;
  }
}

export function useChama() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const pubkey = user?.pubkey;
  const isModerator = pubkey === MODERATOR_PUBKEY;
  const nip44Supported = Boolean(user?.signer.nip44);

  // Local application record, keyed per account and re-read on login switch.
  const storageKey = `chama:application:${pubkey ?? "anon"}`;
  const [application, setApplicationState] = useState<ApplicationRecord | null>(() =>
    readApplication(storageKey),
  );
  useEffect(() => {
    setApplicationState(readApplication(storageKey));
  }, [storageKey]);
  const setApplication = (record: ApplicationRecord | null) => {
    setApplicationState(record);
    try {
      if (record) localStorage.setItem(storageKey, JSON.stringify(record));
      else localStorage.removeItem(storageKey);
    } catch {
      /* storage full or blocked — the record is best-effort */
    }
  };

  // The member's own Community List (their vault — the source of truth).
  const listQuery = useQuery({
    queryKey: ["chama", "community-list", pubkey],
    enabled: Boolean(pubkey && nip44Supported),
    queryFn: async (c) => {
      const events = await nostr.query(
        [{ kinds: [KIND_COMMUNITY_LIST], authors: [pubkey!] }],
        { signal: c.signal },
      );
      return decryptCommunityList(events, user!.signer, pubkey!);
    },
    staleTime: 30_000,
  });

  // Direct Invites addressed to me (Concord's indexed invite lane).
  const invitesQuery = useQuery({
    queryKey: ["chama", "invites", pubkey],
    enabled: Boolean(pubkey && nip44Supported),
    queryFn: async (c) => {
      const events = await nostr.query(
        [{ kinds: [1059], "#p": [pubkey!], "#k": [String(KIND_DIRECT_INVITE)] }],
        { signal: c.signal },
      );
      const out: ReceivedInvite[] = [];
      for (const e of events) {
        const inv = await unwrapDirectInvite(e, user!.signer);
        if (inv) out.push(inv);
      }
      return out;
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  // Decline notices addressed to me.
  const responsesQuery = useQuery({
    queryKey: ["chama", "responses", pubkey],
    enabled: Boolean(pubkey && nip44Supported && !isModerator),
    queryFn: async (c) => {
      const events = await nostr.query(
        [{ kinds: [1059], "#p": [pubkey!], "#k": [String(JOIN_RESPONSE_KIND)] }],
        { signal: c.signal },
      );
      const out: ReceivedJoinResponse[] = [];
      for (const e of events) {
        const r = await unwrapJoinResponse(e, user!.signer);
        if (r && r.from === MODERATOR_PUBKEY) out.push(r);
      }
      return out;
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const membership: Membership | undefined = useMemo(() => {
    const list = listQuery.data?.memberships ?? [];
    // The chama is the community owned by the moderator. The moderator's own
    // List marks them owner; invitees hold the same community_id.
    return (
      list.find((m) => m.current.owner === MODERATOR_PUBKEY) ?? undefined
    );
  }, [listQuery.data]);

  const invite: ReceivedInvite | undefined = useMemo(() => {
    if (membership) return undefined;
    return invitesQuery.data?.find((i) => i.bundle.owner === MODERATOR_PUBKEY);
  }, [invitesQuery.data, membership]);

  const decline: ReceivedJoinResponse | undefined = useMemo(() => {
    if (membership || invite) return undefined;
    return responsesQuery.data?.[0];
  }, [responsesQuery.data, membership, invite]);

  const status: ChamaStatus = useMemo(() => {
    if (!pubkey) return "logged-out";
    if (!nip44Supported) return "unsupported";
    if (listQuery.isLoading || invitesQuery.isLoading) return "loading";
    if (membership) return "member";
    if (invite) return "invited";
    // A decline only rules while it's newer than the last application —
    // reapplying returns the user to "pending".
    if (decline && (!application || application.at <= decline.createdAt * 1000)) {
      return "declined";
    }
    if (application) return "pending";
    if (decline) return "declined";
    return "stranger";
  }, [pubkey, nip44Supported, listQuery.isLoading, invitesQuery.isLoading, membership, invite, decline, application]);

  return {
    status,
    isModerator,
    nip44Supported,
    membership,
    invite,
    decline,
    application,
    setApplication,
    communityList: listQuery.data ?? EMPTY_LIST,
    refetchAll: () => {
      void listQuery.refetch();
      void invitesQuery.refetch();
      void responsesQuery.refetch();
    },
  };
}

/** Relays the community lives on (membership list ∪ app defaults). */
export function communityRelays(membership: Membership | undefined): string[] {
  const urls = new Set<string>(COMMUNITY_RELAYS);
  for (const r of membership?.current.relays ?? []) urls.add(r);
  return [...urls];
}
